import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppShell } from '../components/AppShell';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { PageContainer } from '../components/ui/PageContainer';
import { AttentionList } from '../components/home/AttentionList';
import { BusinessCaseList } from '../components/home/BusinessCaseList';
import { HomeSection, TextLink } from '../components/home/HomeSection';
import { ServiceCard } from '../components/home/ServiceCard';
import { BuildingsSummary } from '../components/buildings/BuildingsSummary';
import { decorateBuilding, summarizePortfolio } from '../components/buildings/buildingMetrics';
import { AGREEMENT_STATUS } from '../config/contracts';
import { roleLabels } from '../config/roles';
import { sortServices } from '../config/serviceCategories';
import { useAccess } from '../hooks/useAccess';
import { useAuth } from '../hooks/useAuth';
import { useBuildings } from '../hooks/useBuildings';
import { usePermissions } from '../hooks/usePermissions';
import { useTenant } from '../hooks/useTenant';
import { agreementsAPI, notificationsAPI, platformAPI } from '../services/api';
import { relativeTime } from '../services/relativeTime';

const MAX_SERVICES = 9;
const MAX_NOTIFICATIONS = 3;
const NEEDS_ATTENTION_LINK = '/buildings?performance=attention&sort=performance';

export const greetingFor = (hour) => {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const firstNameOf = (user) =>
  String(user?.full_name || '').trim().split(/\s+/)[0] || user?.email?.split('@')[0] || '';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const accessGapDetail = (diagnostics) => {
  const unpaired = diagnostics?.personasWithoutBusinessCase || [];
  const idle = diagnostics?.businessCasesWithoutPersona || [];
  if (unpaired.length > 0) {
    return `Your role (${unpaired.map((persona) => persona.label).join(', ')}) is not assigned in any Business Case. Ask an administrator to add you.`;
  }
  if (idle.length > 0) {
    return `You belong to ${idle.join(', ')}, but have no role assigned there. Ask an administrator to assign one.`;
  }
  return 'Ask an administrator to assign you a role in a Business Case.';
};

/**
 * Home — what the signed-in user can act on, built only from real data and
 * their access: items needing attention, the WP3 services their Business
 * Cases grant, their buildings at a glance and those Business Cases. Each
 * section shows only with the permission behind it.
 */
export const Home = () => {
  const navigate = useNavigate();
  const { user, primaryRole } = useAuth();
  const { personas, activePersona, businessCases, diagnostics, bypass, loading: accessLoading } = useAccess();
  const { activeTenant } = useTenant();
  const { has, loading: permissionsLoading } = usePermissions();
  const canReadBuildings = has('building:read');
  const canReadAgreements = has('agreement:read');

  const { buildings } = useBuildings({ enabled: !permissionsLoading && canReadBuildings });
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [agreements, setAgreements] = useState([]);

  // The catalogue depends on the persona the user is acting as.
  useEffect(() => {
    if (accessLoading) return undefined;
    let cancelled = false;
    setServicesLoading(true);
    platformAPI
      .listWp3Services()
      .then((data) => { if (!cancelled) setServices(data?.services || []); })
      .catch(() => { if (!cancelled) setServices([]); })
      .finally(() => { if (!cancelled) setServicesLoading(false); });
    return () => { cancelled = true; };
  }, [accessLoading, activePersona]);

  useEffect(() => {
    let cancelled = false;
    notificationsAPI
      .list()
      .then((data) => { if (!cancelled) setNotifications(Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (permissionsLoading || !canReadAgreements) return undefined;
    let cancelled = false;
    agreementsAPI
      .listAgreements()
      .then((data) => { if (!cancelled) setAgreements(Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [permissionsLoading, canReadAgreements]);

  const markRead = async (id) => {
    setNotifications((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await notificationsAPI.markRead(id);
      window.dispatchEvent(new Event('notifications:refresh')); // keeps the header bell in step
    } catch {
      setNotifications((list) => list.map((n) => (n.id === id ? { ...n, read: false } : n)));
      toast.error('Could not mark the notification as read');
    }
  };

  const summary = useMemo(
    () => summarizePortfolio((canReadBuildings ? buildings || [] : []).map(decorateBuilding)),
    [buildings, canReadBuildings]
  );
  const sortedServices = useMemo(() => sortServices(services), [services]);
  const businessCaseNames = Object.fromEntries((businessCases || []).map((bc) => [bc.code, bc.name]));
  const agreementsInProgress = agreements.filter(
    (a) => a.status === AGREEMENT_STATUS.PENDING || a.status === AGREEMENT_STATUS.COUNTERSIGNED
  ).length;
  const unread = notifications.filter((n) => !n.read);
  const noServices = !accessLoading && !servicesLoading && !bypass && services.length === 0;

  const attention = [];
  if (canReadBuildings && summary.needsAttention > 0) {
    attention.push({
      id: 'buildings',
      tone: 'warning',
      title: `${plural(summary.needsAttention, 'building', 'buildings')} below 70% performance`,
      detail: 'Rated Fair or Poor on their performance score.',
      action: <TextLink to={NEEDS_ATTENTION_LINK}>Review</TextLink>,
    });
  }
  if (canReadAgreements && agreementsInProgress > 0) {
    attention.push({
      id: 'agreements',
      tone: 'action',
      title: `${plural(agreementsInProgress, 'data agreement', 'data agreements')} in progress`,
      detail: 'Pending or countersigned; each needs a next step to become active.',
      action: <TextLink to="/data-exchange">Open</TextLink>,
    });
  }
  unread.slice(0, MAX_NOTIFICATIONS).forEach((n) => {
    attention.push({
      id: `notification-${n.id}`,
      tone: 'notice',
      title: n.title,
      detail: n.message,
      when: relativeTime(n.created_at),
      action: (
        <button
          type="button"
          onClick={() => markRead(n.id)}
          className="p-0 bg-transparent border-0 text-[12px] text-lb-muted hover:text-lb-ink hover:underline focus-ring rounded"
        >
          Mark as read
        </button>
      ),
    });
  });
  if (noServices) {
    attention.push({
      id: 'access',
      tone: 'info',
      title: 'No services are assigned to you yet',
      detail: accessGapDetail(diagnostics),
    });
  }

  const firstName = firstNameOf(user);
  const personaLabel = personas.find((persona) => persona.role === activePersona)?.label || roleLabels[primaryRole];
  const context = [
    personaLabel,
    activeTenant?.name,
    businessCases?.length ? plural(businessCases.length, 'Business Case', 'Business Cases') : null,
  ].filter(Boolean).join(' · ');

  return (
    <AppShell title="Home" isHome>
      <PageContainer>
        <header className="flex flex-col gap-1 mb-1">
          <h1 className="m-0 text-[28px] font-semibold tracking-[-0.02em] leading-[1.2] text-lb-ink-strong">
            {greetingFor(new Date().getHours())}{firstName ? `, ${firstName}` : ''}
          </h1>
          {context && <p className="m-0 text-[14px] text-lb-muted">{context}</p>}
        </header>

        <HomeSection title="Needs your attention" count={attention.length}>
          {accessLoading || permissionsLoading ? <SkeletonLoader rows={2} /> : <AttentionList items={attention} />}
        </HomeSection>

        <HomeSection
          title="Your services"
          count={services.length}
          action={services.length > 0 && (
            <TextLink to="/services">
              {services.length > MAX_SERVICES ? `All ${services.length} services` : 'All services'}
            </TextLink>
          )}
        >
          {accessLoading || servicesLoading ? (
            <SkeletonLoader rows={3} />
          ) : sortedServices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {sortedServices.slice(0, MAX_SERVICES).map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  businessCaseNames={businessCaseNames}
                  onOpen={() => navigate(`/services?service=${encodeURIComponent(service.id)}`)}
                />
              ))}
            </div>
          ) : (
            <div className="card-base px-4 py-4 flex items-center justify-between gap-3 flex-wrap text-[13px] text-lb-muted">
              No services yet.
              <TextLink to="/wiki">How access to services works</TextLink>
            </div>
          )}
        </HomeSection>

        {canReadBuildings && summary.count > 0 && (
          <HomeSection title="Your buildings" action={<TextLink to="/buildings">Open buildings</TextLink>}>
            <BuildingsSummary
              summary={summary}
              onShowNeedsAttention={() => navigate(NEEDS_ATTENTION_LINK)}
              onShowEpc={(rating) => navigate(`/buildings?epc=${encodeURIComponent(rating)}`)}
            />
          </HomeSection>
        )}

        {businessCases?.length > 0 && (
          <HomeSection title="Your Business Cases" count={businessCases.length}>
            <BusinessCaseList businessCases={businessCases} services={services} />
          </HomeSection>
        )}
      </PageContainer>
    </AppShell>
  );
};
